import React, { useState, useEffect } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { Loader2, MousePointer } from 'lucide-react';
import { toast } from 'sonner';
import DuplicateWarningDialog from '../common/DuplicateWarningDialog';

import { useAuth } from '../../context/AuthContext';

export const EquipmentModal = ({ 
  open, 
  onOpenChange, 
  equipment, 
  categories, 
  api, 
  onSuccess 
}) => {
  const { isAdmin } = useAuth();
  const isAdminUser = typeof isAdmin === 'function' ? isAdmin() : false;
  const [form, setForm] = useState({
    name: '',
    category_id: '',
    notes: '',
    serial_selection_mode: 'manual',
    created_by: ''
  });
  const [saving, setSaving] = useState(false);
  const [duplicateError, setDuplicateError] = useState(null);
  const [users, setUsers] = useState([]);

  const performCreate = async (forceCreate = false) => {
    const apiInstance = api();
    const url = forceCreate ? '/equipment?force_create=true' : '/equipment';
    return apiInstance.post(url, form);
  };

  useEffect(() => {
    if (!open) return;
    // v5.4.12 — load active users for the owner dropdown
    api().get('/users').then((res) => {
      const list = Array.isArray(res.data) ? res.data : (res.data.users || []);
      setUsers(list.filter((u) => u.status === 'active'));
    }).catch(() => setUsers([]));
  }, [open, api]);

  useEffect(() => {
    if (equipment) {
      setForm({
        name: equipment.name,
        category_id: equipment.category_id,
        notes: equipment.notes || '',
        serial_selection_mode: equipment.serial_selection_mode || 'manual',
        created_by: equipment.created_by || ''
      });
    } else {
      setForm({ name: '', category_id: '', notes: '', serial_selection_mode: 'manual', created_by: '' });
    }
  }, [equipment, open]);

  const handleSave = async () => {
    if (!form.name || !form.category_id) {
      toast.error('Nombre y categoría son requeridos');
      return;
    }

    setSaving(true);
    try {
      const apiInstance = api();
      if (equipment) {
        await apiInstance.put(`/equipment/${equipment.id}`, form);
        toast.success('Equipo actualizado');
      } else {
        await performCreate(false);
        toast.success('Equipo creado');
      }
      onSuccess?.();
      onOpenChange(false);
    } catch (error) {
      const detail = error.response?.data?.detail;
      if (error.response?.status === 409 && detail && typeof detail === 'object' && detail.duplicate) {
        setDuplicateError(detail);
      } else {
        toast.error(typeof detail === 'string' ? detail : 'Error al guardar');
      }
    } finally {
      setSaving(false);
    }
  };

  const handleForceCreate = async () => {
    setDuplicateError(null);
    setSaving(true);
    try {
      await performCreate(true);
      toast.success('Equipo creado (duplicado forzado)');
      onSuccess?.();
      onOpenChange(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    setForm({ name: '', category_id: '', notes: '', serial_selection_mode: 'manual' });
    onOpenChange(false);
  };

  return (
    <>
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{equipment ? 'Editar Equipo' : 'Nuevo Equipo'}</DialogTitle>
          <DialogDescription>Define el tipo de equipo (las unidades individuales se agregan después)</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label>Nombre del Equipo *</Label>
            <Input 
              value={form.name} 
              onChange={(e) => setForm({ ...form, name: e.target.value })} 
              placeholder="Ej: Laptop Dell Latitude 5520" 
            />
          </div>
          <div className="space-y-2">
            <Label>Categoría *</Label>
            <Select value={form.category_id} onValueChange={(v) => setForm({ ...form, category_id: v })}>
              <SelectTrigger><SelectValue placeholder="Seleccionar" /></SelectTrigger>
              <SelectContent>
                {categories.map(cat => (
                  <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          {/* Serial Selection Mode - Always manual */}
          <div className="space-y-2">
            <Label>Modo de Selección de Números de Serie</Label>
            <div className="p-3 rounded-lg border-2 border-primary bg-primary/10 text-left">
              <div className="flex items-center gap-2 mb-1">
                <MousePointer className="h-4 w-4" />
                <span className="font-medium text-sm">Manual</span>
              </div>
              <p className="text-xs text-muted-foreground">
                El usuario elige los seriales al registrar salida
              </p>
            </div>
          </div>

          <div className="space-y-2">
            <Label>Notas</Label>
            <Textarea 
              value={form.notes} 
              onChange={(e) => setForm({ ...form, notes: e.target.value })} 
              placeholder="Descripción general del equipo"
              rows={2}
            />
          </div>

          {/* v5.4.14 — Owner field is admin-only. Hidden for regular users. */}
          {isAdminUser && (
            <div className="space-y-2">
              <Label>Owner (Creador del equipo)</Label>
              <Select
                value={form.created_by || '__none__'}
                onValueChange={(v) => setForm({ ...form, created_by: v === '__none__' ? '' : v })}
              >
                <SelectTrigger data-testid="equipment-owner-select"><SelectValue placeholder="Sin owner" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">Sin owner</SelectItem>
                  {users.map((u) => (
                    <SelectItem key={u.id} value={u.id}>
                      {u.name} <span className="text-xs text-muted-foreground">({u.role})</span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-[11px] text-muted-foreground">
                Solo visible para admins. Se usa exclusivamente para enrutar alertas de unidades huérfanas a quien las pueda arreglar.
              </p>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={handleClose}>Cancelar</Button>
          <Button onClick={handleSave} disabled={saving} className="btn-primary">
            {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            {equipment ? 'Guardar' : 'Crear'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>

    <DuplicateWarningDialog
      duplicateError={duplicateError}
      onCancel={() => setDuplicateError(null)}
      onForceCreate={handleForceCreate}
      entityLabel="equipo"
    />
  </>
  );
};

export default EquipmentModal;
